/* ---------------------------------------------------------------------------
   PACKS PICKER  (<packs-picker>)
   ---------------------------------------------------------------------------
   Multi-pack selector for products carrying a "custom.packs" metafield.
   Picking a pack multiplies the quantity that is submitted to /cart/add and
   re-renders the price with the pack tier discount (10/20/25/30%).

   Why this is a custom element in an external asset (it used to be an inline
   script inside snippets/packs-picker.liquid, initialised once per page):

     * In the quick buy modal it gave up before binding anything: it required the
       pack quantity stepper, and the modal renders no quantity selector block.
       The pack links stayed plain anchors, so clicking one only put "#20" in the
       address bar.
     * <product-rerender> replaces the picker markup on every variant change, in
       the modal and on the product page alike. Listeners bound to the old markup
       died with it and the selected pack fell back to the first one.
     * A global "already initialised" flag plus document-wide queries meant a
       second picker on the page (quick buy opened from a product page) could
       never start, or drove the wrong product.

   A custom element wires itself up in connectedCallback, so every copy of the
   picker works: product page, featured product and quick buy modal alike.

   Markup contract (see snippets/packs-picker.liquid):
     <packs-picker packs="1,5,10" form-id="..." [sync-url]>
       <a class="block-swatch" data-pack-count="5" data-pack-discount="10">…</a>
   Ancestor: .product-info[data-has-packs][data-base-product-price][data-base-product-currency]
--------------------------------------------------------------------------- */
(function () {
  "use strict";

  if (window.customElements.get("packs-picker")) return;

  function formatMoney(cents, fallbackSymbol) {
    var settings = window.themeVariables && window.themeVariables.settings;
    var format =
      settings &&
      (settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat);

    if (!format) {
      return fallbackSymbol + " " + (cents / 100).toFixed(2).replace(".", ",");
    }

    var amount = (cents / 100).toFixed(2);
    var amountWithComma = amount.replace(".", ",");
    var amountNoDecimals = String(Math.round(cents / 100));

    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        amountNoDecimals,
      )
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  // Replaces the price text while keeping the visually hidden label in place.
  function setPriceElementText(el, srOnlyText, priceText) {
    var label = el.querySelector(".sr-only");
    el.textContent = "";

    if (label) {
      el.appendChild(label);
    } else if (srOnlyText) {
      var sr = document.createElement("span");
      sr.className = "sr-only";
      sr.textContent = srOnlyText;
      el.appendChild(sr);
    }

    el.appendChild(document.createTextNode(priceText));
  }

  class PacksPicker extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();

      var signal = this.abortController.signal;

      this.productInfo = this.closest(".product-info[data-has-packs]");
      if (!this.productInfo) return;

      this.validPacks = (
        this.getAttribute("packs") ||
        this.productInfo.dataset.packs ||
        ""
      )
        .split(",")
        .map(function (value) {
          return value.trim();
        })
        .filter(Boolean);

      if (!this.validPacks.length) return;

      this.strings = {
        regularPrice: this.getAttribute("regular-price-label") || "",
        salePrice: this.getAttribute("sale-price-label") || "",
        perTin: this.getAttribute("per-tin-template") || "",
      };

      // The pack quantity stepper only exists when the merchant added a quantity
      // selector block (product page). The quick buy modal has none, so the pack
      // count alone drives the quantity.
      this.packQtyInput = this.productInfo.querySelector(
        "[data-pack-quantity]",
      );
      this.packQtySelector = this.productInfo.querySelector(
        "[data-pack-quantity-selector]",
      );
      this.decreaseButton =
        this.packQtySelector &&
        this.packQtySelector.querySelector("[data-pack-qty-decrease]");
      this.increaseButton =
        this.packQtySelector &&
        this.packQtySelector.querySelector("[data-pack-qty-increase]");

      this.quantityInput =
        this.productInfo.querySelector(
          'quantity-selector input[name="quantity"]',
        ) || this.createQuantityInput();
      if (!this.quantityInput) return;

      this.form =
        document.getElementById(this.getAttribute("form-id") || "") ||
        this.quantityInput.form ||
        this.productInfo.querySelector("product-form form");

      this.selectedPack = this.restoreSelection();
      this.renderSelection();
      this.productInfo.dataset.packsLoaded = "true";

      this.addEventListener("click", this.onPickerClick.bind(this), {
        signal: signal,
      });

      if (this.packQtyInput) {
        this.packQtyInput.addEventListener("input", this.sync.bind(this), {
          signal: signal,
        });
        this.packQtyInput.addEventListener("change", this.sync.bind(this), {
          signal: signal,
        });
      }

      // Capture phase + stopImmediatePropagation so <quantity-selector> does not
      // also act on the click: the stepper counts packs, not individual tins.
      if (this.decreaseButton) {
        this.decreaseButton.addEventListener(
          "click",
          this.onPackQtyStep.bind(this, -1),
          {
            capture: true,
            signal: signal,
          },
        );
      }

      if (this.increaseButton) {
        this.increaseButton.addEventListener(
          "click",
          this.onPackQtyStep.bind(this, 1),
          {
            capture: true,
            signal: signal,
          },
        );
      }

      if (this.form) {
        this.form.addEventListener(
          "variant:change",
          this.onVariantChange.bind(this),
          { signal: signal },
        );
      }

      this.sync();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    // Quick buy has no quantity selector block, so the pack count is carried by a
    // hidden input associated to the product form. It is a child of the picker, so
    // it is removed together with it.
    createQuantityInput() {
      var formId = this.getAttribute("form-id");
      var form = formId
        ? document.getElementById(formId)
        : this.productInfo.querySelector("product-form form");

      // NOTE: form.id would return the <input name="id"> the form contains, not the attribute.
      formId = form && form.getAttribute("id");
      if (!formId) return null;

      var input = this.querySelector('input[name="quantity"]');

      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "quantity";
        input.value = "1";
        input.setAttribute("form", formId);
        this.appendChild(input);
      }

      return input;
    }

    get packLinks() {
      return Array.from(this.querySelectorAll("a.block-swatch"));
    }

    // The selection has to outlive <product-rerender>, which replaces this markup on
    // every variant change: from the URL on the product page, from the modal element
    // itself in quick buy (only its inner content is re-rendered).
    get selectionStore() {
      if (this.hasAttribute("sync-url")) return null;

      return (
        this.closest("quick-buy-modal") || this.closest("product-rerender")
      );
    }

    restoreSelection() {
      var stored = this.hasAttribute("sync-url")
        ? new URL(window.location.href).searchParams.get("packs")
        : this.selectionStore && this.selectionStore.dataset.selectedPack;

      var pack =
        this.validPacks.indexOf(stored) === -1 ? this.validPacks[0] : stored;
      this.persistSelection(pack);

      return pack;
    }

    persistSelection(pack) {
      if (this.hasAttribute("sync-url")) {
        var url = new URL(window.location.href);
        url.searchParams.set("packs", pack);
        window.history.replaceState({}, "", url);
      } else if (this.selectionStore) {
        this.selectionStore.dataset.selectedPack = pack;
      }
    }

    renderSelection() {
      var selectedPack = this.selectedPack;

      this.packLinks.forEach(function (link) {
        link.classList.toggle(
          "is-selected",
          link.dataset.packCount === selectedPack,
        );
      });
    }

    onPickerClick(event) {
      var link = event.target.closest("a.block-swatch");
      if (!link) return;

      event.preventDefault();

      var pack = link.dataset.packCount;
      if (this.validPacks.indexOf(pack) === -1) return;

      this.selectedPack = pack;
      this.persistSelection(pack);
      this.renderSelection();
      this.sync();
    }

    onPackQtyStep(delta, event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      this.packQtyInput.value = Math.max(
        1,
        (parseInt(this.packQtyInput.value, 10) || 1) + delta,
      );
      this.sync();
    }

    onVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      this.productInfo.dataset.baseProductPrice = variant.price;
      this.sync();
    }

    sync() {
      var selectedLink = this.querySelector(
        'a.block-swatch[data-pack-count="' + this.selectedPack + '"]',
      );
      if (!selectedLink) return;

      var packCount = parseInt(this.selectedPack, 10) || 1;
      var packQty = 1;

      if (this.packQtyInput) {
        packQty = Math.max(1, parseInt(this.packQtyInput.value, 10) || 1);
        this.packQtyInput.value = packQty;
        if (this.decreaseButton) this.decreaseButton.disabled = packQty <= 1;
      }

      var tinCount = packCount * packQty;

      this.quantityInput.value = tinCount;
      this.quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event("change", { bubbles: true }));

      this.updatePrices(
        parseInt(selectedLink.dataset.packDiscount, 10) || 0,
        tinCount,
      );
    }

    updatePrices(discount, tinCount) {
      var basePriceCents =
        parseInt(this.productInfo.dataset.baseProductPrice, 10) || 0;
      if (!basePriceCents) return;

      var fallbackSymbol = this.productInfo.dataset.baseProductCurrency || "";
      var perTinCents =
        discount > 0
          ? basePriceCents - Math.floor((basePriceCents * discount) / 100)
          : basePriceCents;

      var self = this;

      this.priceLists().forEach(function (priceList) {
        self.updatePriceList(priceList, {
          fullCents: tinCount * basePriceCents,
          saleCents: perTinCents * tinCount,
          perTinCents: perTinCents,
          discount: discount,
          tinCount: tinCount,
          fallbackSymbol: fallbackSymbol,
        });
      });
    }

    // The price block, plus the compact price the quick buy modal shows on mobile.
    priceLists() {
      var root = this.closest("product-rerender") || this.productInfo;

      return Array.from(
        root.querySelectorAll(
          '[data-block-type="price"] price-list, .quick-buy-modal__mobile-info price-list',
        ),
      );
    }

    updatePriceList(priceList, prices) {
      var salePriceEl = priceList.querySelector("sale-price");
      if (!salePriceEl) return;

      // A variant can carry its own compare at price. It is left untouched and only
      // hidden while a pack discount is shown, so it comes back for a single tin.
      var variantCompareAtEl = priceList.querySelector(
        "compare-at-price:not([data-packs-compare])",
      );
      var packCompareAtEl = priceList.querySelector(
        "compare-at-price[data-packs-compare]",
      );

      if (prices.discount > 0) {
        if (!packCompareAtEl) {
          packCompareAtEl = document.createElement("compare-at-price");
          packCompareAtEl.setAttribute("data-packs-compare", "true");
          priceList.insertBefore(packCompareAtEl, salePriceEl);
        }

        packCompareAtEl.className = salePriceEl.className
          .replace("text-on-sale", "text-subdued")
          .replace("h4", "h5");
        packCompareAtEl.classList.add("line-through");
        setPriceElementText(
          packCompareAtEl,
          this.strings.regularPrice,
          formatMoney(prices.fullCents, prices.fallbackSymbol),
        );

        if (variantCompareAtEl) variantCompareAtEl.hidden = true;
      } else {
        if (packCompareAtEl) packCompareAtEl.remove();
        if (variantCompareAtEl) variantCompareAtEl.hidden = false;
      }

      var onSale = prices.discount > 0 || variantCompareAtEl != null;
      salePriceEl.classList.toggle("text-on-sale", onSale);
      salePriceEl.classList.toggle("text-subdued", !onSale);
      setPriceElementText(
        salePriceEl,
        this.strings.salePrice,
        formatMoney(prices.saleCents, prices.fallbackSymbol),
      );

      this.updatePerTinPrice(priceList, prices);
    }

    updatePerTinPrice(priceList, prices) {
      var unitPriceEl = priceList.querySelector("unit-price");

      // A genuine unit price (per ml, per g…) belongs to the variant, not to us.
      if (unitPriceEl && unitPriceEl.getAttribute("data-packs-unit") !== "true")
        return;

      // Only the full size price block has room for it, and only multi-tin packs need it.
      var showPerTin =
        prices.tinCount > 1 &&
        this.strings.perTin &&
        priceList.classList.contains("price-list--product");

      if (!showPerTin) {
        if (unitPriceEl) unitPriceEl.remove();
        return;
      }

      if (!unitPriceEl) {
        unitPriceEl = document.createElement("unit-price");
        unitPriceEl.setAttribute("data-packs-unit", "true");
        unitPriceEl.className = "text-subdued per-pack";
        priceList.appendChild(unitPriceEl);
      }

      unitPriceEl.textContent = this.strings.perTin.replace(
        "@@PRICE@@",
        formatMoney(prices.perTinCents, prices.fallbackSymbol),
      );
    }
  }

  window.customElements.define("packs-picker", PacksPicker);
})();
